import type { Comment } from '@service-storage/generated/schemas/comment';
import type { CommentThread } from '@service-storage/generated/schemas/commentThread';
import type { CreateCommentResponse } from '@service-storage/generated/schemas/createCommentResponse';
import type { CreateUnthreadedAnchorResponse } from '@service-storage/generated/schemas/createUnthreadedAnchorResponse';
import type { DeleteCommentResponse } from '@service-storage/generated/schemas/deleteCommentResponse';
import type { DeleteUnthreadedAnchorResponse } from '@service-storage/generated/schemas/deleteUnthreadedAnchorResponse';
import type { EditAnchorResponse } from '@service-storage/generated/schemas/editAnchorResponse';
import type { EditCommentResponse } from '@service-storage/generated/schemas/editCommentResponse';
import {
  type Accessor,
  batch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
} from 'solid-js';
import { createStore, produce, reconcile } from 'solid-js/store';
import type { IHighlight } from '../model/Highlight';
import { getPdfAnchors, getPdfComments } from '../queries/annotations';

export type HighlightUuidMap = Partial<Record<string, IHighlight>>;
export type HighlightPageMap = Partial<Record<number, HighlightUuidMap>>;

export function createPdfAnnotations(documentId: Accessor<string>) {
  const [highlightsByPage, setHighlightsByPage] = createStore<HighlightPageMap>(
    {}
  );
  const [convertedHighlightDraftId, setConvertedHighlightDraftId] =
    createSignal<string>();
  const [commentThreadsResource, { mutate: mutateCommentThreads }] =
    createResource(documentId, getPdfComments);
  const [anchorsResource, { mutate: mutateAnchors }] = createResource(
    documentId,
    getPdfAnchors
  );

  const highlightsByUuid = createMemo(() => {
    const result: HighlightUuidMap = {};
    for (const pageHighlights of Object.values(highlightsByPage)) {
      if (!pageHighlights) continue;
      for (const [uuid, highlight] of Object.entries(pageHighlights)) {
        result[uuid] = highlight;
      }
    }
    return result;
  });

  const hasHighlights = () =>
    Object.values(highlightsByPage).some(
      (pageHighlights) => Object.values(pageHighlights ?? {}).length > 0
    );

  createEffect(() => {
    setHighlightsByPage(reconcile({}));

    const anchors = anchorsResource();
    if (!anchors || anchors.length === 0) return;

    const commentThreads = commentThreadsResource() ?? [];
    const highlightAnchors = anchors.filter(
      (anchor) => anchor.anchorType === 'highlight'
    );

    const mappedAnchors = highlightAnchors.flatMap((anchor) => {
      const commentThread = commentThreads.find(
        (thread) => thread.thread.threadId === anchor.threadId
      );
      if (!commentThread && anchor.threadId) return [];

      const highlight: IHighlight = {
        owner: anchor.owner,
        existsOnServer: true,
        pageNum: anchor.page,
        rects: anchor.highlightRects,
        color: {
          red: anchor.red,
          green: anchor.green,
          blue: anchor.blue,
          alpha: anchor.alpha,
        },
        hasTempThread: false,
        uuid: anchor.uuid,
        text: anchor.text,
        type: anchor.highlightType,
        pageViewport: {
          width: anchor.pageViewportWidth,
          height: anchor.pageViewportHeight,
        },
        thread: commentThread
          ? {
              threadId: commentThread.thread.threadId,
              rootId: commentThread.comments[0].commentId,
              anchorId: anchor.uuid,
              page: anchor.page,
              comments: commentThread.comments,
              isResolved: commentThread.thread.resolved,
            }
          : null,
      };

      return highlight;
    });

    batch(() => {
      for (const highlight of mappedAnchors) {
        setHighlightsByPage(highlight.pageNum, (previous) => ({
          ...previous,
          [highlight.uuid]: highlight,
        }));
      }
    });
  });

  const commands = {
    applyCreatedAnchor(response: CreateUnthreadedAnchorResponse) {
      mutateAnchors((previous) => [...(previous ?? []), response]);
    },
    applyDeletedAnchor(response: DeleteUnthreadedAnchorResponse) {
      mutateAnchors((previous) =>
        (previous ?? []).filter((anchor) => anchor.uuid !== response.uuid)
      );
      if (response.threadId != null) {
        mutateCommentThreads((previous) =>
          (previous ?? []).filter(
            (thread) => thread.thread.threadId !== response.threadId
          )
        );
      }
    },
    applyEditedAnchor(response: EditAnchorResponse) {
      mutateAnchors((previous) => [
        ...(previous ?? []).filter((anchor) => anchor.uuid !== response.uuid),
        response,
      ]);
    },
    applyCreatedComment(response: CreateCommentResponse) {
      const commentThread: CommentThread = {
        thread: response.thread,
        comments: response.comments,
      };
      const anchor = response.anchor;
      batch(() => {
        if (anchor) {
          mutateAnchors((previous) => [...(previous ?? []), anchor]);
        }

        mutateCommentThreads((previous) => {
          let replacedExistingThread = false;
          const next = (previous ?? []).map((thread) => {
            if (thread.thread.threadId !== commentThread.thread.threadId) {
              return thread;
            }
            replacedExistingThread = true;
            return commentThread;
          });
          if (!replacedExistingThread) next.push(commentThread);
          return next;
        });
      });
    },
    applyEditedComment(response: EditCommentResponse) {
      mutateCommentThreads((previous) =>
        (previous ?? []).map((thread) => {
          if (thread.thread.threadId !== response.threadId) return thread;
          return {
            thread: thread.thread,
            comments: thread.comments.map((comment) => {
              if (comment.commentId !== response.commentId) return comment;
              const editedComment: Comment = response;
              return editedComment;
            }),
          };
        })
      );
    },
    applyDeletedComment(response: DeleteCommentResponse) {
      batch(() => {
        if (response.thread.deleted) {
          mutateCommentThreads((previous) =>
            (previous ?? []).filter(
              (thread) => thread.thread.threadId !== response.thread.threadId
            )
          );
        } else {
          mutateCommentThreads((previous) =>
            (previous ?? []).map((thread) => {
              if (thread.thread.threadId !== response.thread.threadId) {
                return thread;
              }
              return {
                thread: thread.thread,
                comments: thread.comments.filter(
                  (comment) => comment.commentId !== response.commentId
                ),
              };
            })
          );
        }

        if (response.anchor?.deleted) {
          mutateAnchors((previous) =>
            (previous ?? []).filter(
              (anchor) => anchor.uuid !== response.anchor?.uuid
            )
          );
        }
      });
    },
    beginExistingHighlightCommentDraft(highlight: IHighlight) {
      batch(() => {
        setConvertedHighlightDraftId(highlight.uuid);
        setHighlightsByPage(highlight.pageNum, highlight.uuid, (previous) => ({
          ...previous,
          hasTempThread: true,
        }));
      });
    },
    beginNewHighlightCommentDrafts(highlights: IHighlight[]) {
      setHighlightsByPage(
        produce((state) => {
          for (const highlight of highlights) {
            const pageHighlights = state[highlight.pageNum];
            const temporaryHighlight = {
              ...highlight,
              hasTempThread: true,
            };
            if (pageHighlights) {
              pageHighlights[highlight.uuid] = temporaryHighlight;
            } else {
              state[highlight.pageNum] = {
                [highlight.uuid]: temporaryHighlight,
              };
            }
          }
        })
      );
    },
    cancelTemporaryHighlightCommentDraft(uuid: string) {
      const highlight = highlightsByUuid()[uuid];
      const convertedExistingHighlight = convertedHighlightDraftId() === uuid;
      if (!highlight) {
        if (convertedExistingHighlight) setConvertedHighlightDraftId(undefined);
        return convertedExistingHighlight;
      }

      if (convertedExistingHighlight) {
        batch(() => {
          setConvertedHighlightDraftId(undefined);
          setHighlightsByPage(
            highlight.pageNum,
            uuid,
            (previous) =>
              previous && {
                ...previous,
                thread: null,
                hasTempThread: false,
              }
          );
        });
        return true;
      }

      setHighlightsByPage(
        highlight.pageNum,
        produce((pageHighlights) => {
          if (!pageHighlights) return;
          delete pageHighlights[uuid];
        })
      );
      return false;
    },
  };

  return {
    anchors: () => anchorsResource(),
    commentThreads: () => commentThreadsResource(),
    highlightsByPage,
    highlightsByUuid,
    hasHighlights,
    commands,
  };
}

export type PdfAnnotations = ReturnType<typeof createPdfAnnotations>;
